'use strict';

const DMX = require('dmx');
const console = require('better-console');
const animations = require('../../src/animations');
const groupAnimations = require('../../src/group-animations');
const constants = require('../../src/constants');
const appConfig = require('../../application-configuration');
const firebase = require("firebase");
const findElementIndex = require('./utils/find-element-index');
const findElementIndexes = require('./utils/find-element-indexes');

const { byElement: elements } = constants.elementPorts;

/**
 * Themes that the "new" main loop will cycle through
 * This is somewhat hacky, ignoring the concept of "combinations" that come
 * out of the element-ports file..
 * ? Will need to refactor this to be a bit nicer at some point..
 */
const themeLoop = [
  constants.themes.MODERN,
  constants.themes.ODDBALL,
  constants.themes.CHIC
]

/**
 * Animator class
 *
 * This is the main class that is used to control everything.
 * It will receive "ticks" from the main loop, and then update animation frames accordingly.
 * It will also change animations based upon instructions that is handed to it
 *
 */
class Animator {

  /**
   * @param {{
   *   isViewingOnly: Boolean
   * }} config
   */
  constructor(config = { useTestMainLoop: false }) {
    // Websocket connection for debugging / visualisation purposes
    this.connection = null;
    // How long each animation should run for before switching to the next one
    const lengthOfAnimationInSeconds = 30;
    this.animationDuration = lengthOfAnimationInSeconds * 1000;
    this.themeLoopIndex = 0;

    const defaultConfig = {
      isViewingOnly: false
    };

    this.config = {
      ...defaultConfig,
      ...config
    };

    if (!this.config.isViewingOnly) {
      this.initDMX();
    }

    this.initLoop();
    this.isSlave = false;
  }

  /**
   * Initialise the DMX plugin
   *
   * @returns {null}
   */
  initDMX() {
    this.dmx = new DMX();
    this.universe = this.dmx.addUniverse('demo', 'enttec-open-usb-dmx', appConfig.port);
  }

  /**
    * A new main loop animation that will allow some elements to have their own animations
    * For example, some motifs will just be "on" all the time, while others might be doing a
    * simple animation. The strings will probably be looping through the various animations and combinations
    */
  initLoop() {
    // Save to class variable as it needs to be read later to determine what the current animation ID is, and therefore
    // what the next one will be
    this.currentAnimationId = Object.keys(constants.animationsLoop)[0];
    this.updateMainLoopAnimation(this.currentAnimationId);
  }

  /**
   * A new main loop animation that will allow some elements to have their own animations
   * For example, some motifs will just be "on" all the time, while others might be doing a
   * simple animation. The strings will probably be looping through the various animations and combinations
   * 
   * @param {string} animationId
   */
  updateMainLoopAnimation(animationId) {
    this.animationStartTime = Date.now();
    const animationClass = this.getAnimationClass(animationId);

    console.info(`Playing animation: ${animationClass}`);
    firebase.database().ref().update({
      '/readonly/animation': animationClass,
      'updatingKey': 'secret-sauce'
    })

    this.themeLoopIndex = this.themeLoopIndex + 1 >= themeLoop.length ? 0 : this.themeLoopIndex + 1;

    // ! I don't think I should be sending all of these animations every time the animation changes..
    // ! These should all only be set when transitioning from slave to master, or when first starting the system up
    // ! When it's time to change the currently active animation on the elements that do change, then only those elements
    // ! should be part of the data update..
    const { byElement } = constants.elementPorts;
    const elementNames = Object.keys(byElement);
    const steadyOnElements = [
      constants.elements.SANTA.id,
      constants.elements.SANTA_SLEIGH.id,
      constants.elements.REINDEER_SLEIGH.id,
      constants.elements.SNOWMAN.id,
      constants.elements.STANDING_KANGAROO.id,
      constants.elements.PENGUINS.id,
      constants.elements.DUAL_BELLS_HOLLY.id,
      constants.elements.KOALA.id,
      constants.elements.TRAIN.id,
      constants.elements.RIBBON_BELLS.id,
    ]
    this.elementAnimations = elementNames.map((elementId) => {
      if (steadyOnElements.includes(elementId)) {
        return {
          element: elementId,
          animation: new animations['SteadyOn']()
        }
      }

      const sequentialElements = [
        constants.elements.HOLLY.id,
        constants.elements.SANTA_HAND.id,
        constants.elements.REINDEER_LEGS.id,
        constants.elements.STOCKINGS.id,
        constants.elements.DUAL_BELLS.id,
      ]

      if (sequentialElements.includes(elementId)) {
        return {
          element: elementId,
          animation: new animations['Sequential']()
        }
      }

      if (elementId === constants.elements.MERRY_CHRISTMAS_SIGN.id) {
        return {
          element: elementId,
          animation: new animations['Heartbeat']()
        }
      }

      // This forces Elf tree to be part of all theme groups, without changing the themes availableInThemes array
      // The reason I want to leave that as it is is for when a user is in control, I _think_ I want the Eld tree to only come on when
      // a user has a theme selected that includes Multi
      if (elementId === constants.elements.ELF_TREE_2.id) {
        return {
          element: elementId,
          animation: new animations[animationClass]()
        }
      }

      // Everything else gets assigned the current animation from the animation loop
      if (!constants.elements[elementId].availableInThemes.includes(themeLoop[this.themeLoopIndex])) {
        return {
          element: elementId,
          animation: new animations['Off']()
        }
      }

      return {
        element: elementId,
        animation: new animations[animationClass]()
      }
    });

    // "Make it snow" animation
    this.setAnimation([
      constants.elements.NEON_SNOWFLAKE_2.id,
      constants.elements.NEON_SNOWFLAKE_1.id,
      constants.elements.SNOWFLAKE_ICE_2.id,
      constants.elements.SIMPLE_SNOWFLAKES.id,
      constants.elements.SNOWFLAKE_ICE_1.id,
    ], 'sweep', { speed: 5 } );
    

    this.setAnimation([
      constants.elements.BLOSSOMS_4.id,
      constants.elements.BLOSSOMS_2.id, 
      constants.elements.BLOSSOMS_3.id,
      constants.elements.BLOSSOMS_1.id,
    ], 'sweep', { speed: 1 } );
  }

  /**
   * Call the tick function for each active animation
   *
   * @returns {null}
   */
  tick() {
    this.elementAnimations.forEach(({ animation }) => {
      animation.tick();
    });
  }

  /**
   * Perform the next frame for each elements animation
   *
   * @returns {null}
   */
  performNextAction() {
    const nextValues = this.getNextValues();

    if (Object.keys(nextValues).length > 0) {
      this.sendData(nextValues);
    }

    // Check if it's time to change the main loop animation
    if (this.getIsMaster()) {
      const elapsedTime = Date.now() - this.animationStartTime;
      if (elapsedTime >= this.animationDuration) {
        this.changeAnimation();
        this.animationStartTime = Date.now(); // Reset timer
      }
    }
  }

  /**
   * Take an animation ID and get the class name
   *
   * @param {string} animationId ID of the animation to find the class name for
   *
   * @returns {string}
   */
  getAnimationClass(animationId) {
    return (Array.isArray(constants.animations[animationId])) ?
      Object.values(constants.animations[animationId][0]).pop() :
      constants.animations[animationId];
  }

  /**
   * Take a group animation ID and get the class name
   *
   * @param {string} animationId ID of the group animation to find the class name for
   *
   * @returns {string}
   */
  getGroupAnimationClass(animationId) {
    return Object.values(groupAnimations).find(animation => animation.id === animationId).name
  }

  /**
   * Get the next values for each of the animations that are currently assigned to an element
   *
   * @returns {Record<string, number>}
   */
  getNextValues() {
    const nextData = this.elementAnimations.map(({ element, animation }) => {
      const nextValue = animation.getNext();
      const nextChannelValues = {};

      const channel1 = elements[element][constants.channels.CONST__CH_1];
      if (Array.isArray(channel1)) {
        channel1.forEach(channel => {
          nextChannelValues[channel] = nextValue[0];
        })
      } else if (Number.isInteger(channel1)) {
        nextChannelValues[channel1] = nextValue[0];
      } else {
        // Do nothing, this element doesn't have anything plugged into this port
      }

      const channel2 = elements[element][constants.channels.CONST__CH_2];
      if (Array.isArray(channel2)) {
        channel2.forEach(channel => {
          nextChannelValues[channel] = nextValue[1];
        })
      } else if (Number.isInteger(channel2)) {
        nextChannelValues[channel2] = nextValue[1];
      } else {
        // Do nothing, this element doesn't have anything plugged into this port
      }

      return nextChannelValues;
    });

    // nextData is an array of objects. Using Object.assign and the spread operator, the data can be flattened into a single object
    return Object.assign({}, ...nextData);
  }

  /**
   * Send data to the controller board
   *
   * @param {Object} data
   */
  sendData(data) {
    // console.log(this.debug(data));
    if (!this.config.isViewingOnly) {
      this.universe.update(data);
    }

    // If there is a websocket connection, broadcast the next values so that they might be displayed in browser
    if (this.connection) {
      this.connection.sendUTF(JSON.stringify(data));
    }
  }


  /**
   * Change the active animation
   *
   * @returns {null}
   */
  changeAnimation() {
    const animationKeys = Object.keys(constants.animationsLoop);
    const currentAnimationIndex = animationKeys.findIndex(animation => animation === this.currentAnimationId);
    // If the current index is the last one in the array, then the next animation needs to be the first one
    const nextAnimationIndex = currentAnimationIndex === (animationKeys.length - 1) ? 0 : currentAnimationIndex + 1;

    // Create instance of new animation and assign to class variable
    this.currentAnimationId = animationKeys[nextAnimationIndex];
    this.updateMainLoopAnimation(this.currentAnimationId);
  }

  /**
   * Simple debug function that will return a string containing the name of the element/s, and their values
   *
   * @param {Object} data The "next data" object
   *
   * @returns {string}
   */
  debug(data) {
    // Create a string with the value for each of the elements that are currently on
    // The string will have each element doubled-up, but this is because each element has 2 channels that it outputs on
    return Object.keys(data).map((channel) => {
      // Find the element that is using `channel`
      const elementName = Object.keys(elements).find((elementKey) => [elements[elementKey][constants.channels.CONST__CH_1], elements[elementKey][constants.channels.CONST__CH_2]].includes(parseInt(channel, 10)));
      return `${elementName}: ${data[channel]}`;
    }).join(', ');
  }

  /**
   * Get the current mode the animator is running in
   *
   * @returns {string}
   */
  getCurrentMode() {
    return this.getIsSlave() ? 'slave' : 'master';
  }

  /**
   * Check if the RPi should be running as a slave
   *
   * @returns {boolean}
   */
  getIsSlave() {
    return this.isSlave;
  }

  /**
   * Check if the RPi should be running as a master
   * Effectively an alias for getIsSlave()
   *
   * @returns {boolean}
   */
  getIsMaster() {
    return !this.getIsSlave();
  }

  /**
   * Tell the RPi to be active as a slave or not
   *
   * @param {Boolean} isSlave Flag indicating if the RPi is in slave mode or not
   *
   * @returns {null}
   */
  setIsSlave(isSlave) {
    this.isSlave = isSlave;
  }

  /**
   * Tell the RPi to be active as a master or not
   * Effectively an alias for setIsSlave()
   *
   * @param {Boolean} isMaster Flag indicating if the RPi is in master mode or not
   *
   * @returns {null}
   */
  setIsMaster(isMaster) {
    this.setIsSlave(!isMaster);
  }

  /**
   * Function to change the animation for a specific element
   *
   * @param {string[]} elements     Array of element ID's to change the animation of
   * @param {string}   newAnimation The new animation to change to
   * @param {{}}       [args]       [Optional] Extra arguments to pass to the animation class instance
   *
   * @returns {void}
   */
  setAnimation(elements, newAnimation, args) {
    if (newAnimation === 'simonsays') {
      const animationClass = this.getGroupAnimationClass(newAnimation);
      const newAnimations = this.elementAnimations.map((elementAnimation) => {
        const { element } = elementAnimation;

        const elementIndexes = findElementIndexes(elements, element);
        if (elementIndexes.length === 0) {
          return elementAnimation;
        }

        const simonSaysAnimation = new groupAnimations[animationClass]({ sequencePositions: elementIndexes, totalElements: elements.length, ...args })

        return {
          ...elementAnimation,
          animation: simonSaysAnimation
        }
      })

      this.elementAnimations = newAnimations;
      return;
    }
    if (Object.keys(groupAnimations).map(key => key.toLowerCase()).includes(newAnimation.toLowerCase())) {
      const animationClass = this.getGroupAnimationClass(newAnimation);
      // This is the section where I should throw to a "Group Animation" handler, as I will want to add more than just sweep, like bounce, reverse sweep etc
      const newAnimations = this.elementAnimations.map((elementAnimation) => {
        const { element } = elementAnimation;

        const elementIndex = findElementIndex(elements, element);
        if (elementIndex < 0) {
          return elementAnimation;
        }

        return {
          ...elementAnimation,
          animation: new groupAnimations[animationClass]({ sequencePosition: elementIndex, totalElements: elements.length, ...args })
        }
      });

      this.elementAnimations = newAnimations;

      return;
    }

    const animationClass = this.getAnimationClass(newAnimation);

    const newAnimations = this.elementAnimations.map((animation) => {
      const { element } = animation;

      if (!elements.includes(element)) {
        return animation;
      }

      if (!animations[animationClass]) {
        // ! This is a bug because when I set santa wave or reindeer walk, I'm setting a value to animation in the DB that doesn't exist
        // ! When changing the theme, that will re-apply the animation that is in the database, so at that point it's looking
        // ! for the reindeer_walk or santa_wave animations, which obviously don't exist.
        // ! The following it just temporary as this is the only time that it's an issue, I believe..
        // ! Will have to test what happens when I set up elements with their own animation, then use santa wave, then change theme, does that break the existing animation
        // console.error(`"${newAnimation}" doesn't exist. Sticking with existing animation`);
        return {
          ...animation,
          animation: new animations['SteadyOn'](args)
        }
      }

      return {
        ...animation,
        animation: new animations[animationClass](args)
      }
    });

    this.elementAnimations = newAnimations;
  }

  /**
   * Get the currently set animations
   *
   * @returns {*}
   */
  getElementAnimations() {
    return this.elementAnimations;
  }

  /**
   * Get the current animation for a provided elementID
   *
   * @param {string} elementId The ID of the element to find the animation of
   *
   * @returns {string} The name of the animation that is currently playing
   */
  getElementAnimation(elementId) {
    return this.elementAnimations.find(elementAnimation => elementId === elementAnimation.element).animation;
  }
}

module.exports = Animator;
